import { analytics } from "cc-frontend/error-handling"

/**
 * The analytics context, as analytics.js builds it. We don't construct this ourselves —
 * its env-enrichment plugin already fills in `campaign` (from any `utm_*` param), `page`,
 * `screen`, `locale`, `timezone`, `userAgent`, `userAgentData` and `referrer` on every
 * client-side call.
 *
 * https://segment.com/docs/connections/spec/common/#context
 */
export type AnalyticsContext = Record<string, unknown>

/**
 * This browser's anonymous ID, which is what Amplitude uses as its `device_id`.
 *
 * We hand it to the backend so that server-side events (`User - Create Account`) land on
 * the same device as the client-side events they mirror (`User - Sign Up`). Without it
 * the server ends up on a brand new device ID with no UTM history, and the event gets
 * attributed to the wrong channel.
 *
 * Resolves once analytics.js has loaded, since that's when the ID is settled.
 */
export async function getAnonymousId(): Promise<string | null> {
  const user = await analytics.user()
  return user.anonymousId() ?? null
}

/**
 * Hand back the context analytics.js put on its own events, so the backend can send its
 * events with the same one.
 *
 * A source middleware is how we read it: the context is assembled per-event by the
 * enrichment plugin, so there's nothing to ask for directly — we watch an event go past
 * instead. Called with the first event of the session, which is the one carrying the
 * campaign the user arrived on.
 */
export function onAnalyticsContext(handle: (context: AnalyticsContext) => void) {
  void analytics.addSourceMiddleware(({ payload, next }) => {
    const context = payload?.obj?.context
    if (context) handle(context as AnalyticsContext)
    next(payload)
  })
}
